import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Plus,
  Trash,
  PencilSimple,
  Check,
  X,
  Calendar,
  User,
  CaretDown,
  CaretRight,
} from "@phosphor-icons/react";

const PRIORITY_TONE = {
  high: { bg: "#C94A3D", fg: "#fff", label: "HIGH" },
  medium: { bg: "#A8977B", fg: "#1A2F24", label: "MED" },
  low: { bg: "#efece3", fg: "#606C64", label: "LOW" },
};

const PRIORITY_CYCLE = ["low", "medium", "high"];

export const isOverdue = (dueDate, status) => {
  if (!dueDate || status === "done") return false;
  const today = new Date().toISOString().slice(0, 10);
  return dueDate < today;
};

const formatDue = (dueDate) => {
  if (!dueDate) return null;
  const today = new Date().toISOString().slice(0, 10);
  if (dueDate === today) return "Today";
  const dt = new Date(dueDate);
  return dt.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const TaskRow = ({ task, level = 0, onChange, onAddSubtask, members }) => {
  const [expanded, setExpanded] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({
    title: task.title,
    description: task.description || "",
    assignee_id: task.assignee_id || "",
    due_date: task.due_date || "",
    priority: task.priority,
  });

  const toggle = async () => {
    const next = task.status === "done" ? "open" : "done";
    try {
      await api.patch(`/tasks/${task.id}`, { status: next });
      onChange();
    } catch {
      toast.error("Could not update task");
    }
  };

  const cyclePriority = async () => {
    const idx = PRIORITY_CYCLE.indexOf(task.priority);
    const next = PRIORITY_CYCLE[(idx + 1) % PRIORITY_CYCLE.length];
    await api.patch(`/tasks/${task.id}`, { priority: next });
    onChange();
  };

  const remove = async () => {
    if (!window.confirm("Delete this task and any subtasks?")) return;
    await api.delete(`/tasks/${task.id}`);
    onChange();
  };

  const save = async () => {
    try {
      const payload = { ...form };
      if (payload.assignee_id === "") payload.assignee_id = null;
      if (payload.due_date === "") payload.due_date = null;
      await api.patch(`/tasks/${task.id}`, payload);
      setEditing(false);
      onChange();
      toast.success("Task updated.");
    } catch {
      toast.error("Update failed");
    }
  };

  const tone = PRIORITY_TONE[task.priority] || PRIORITY_TONE.medium;
  const overdue = isOverdue(task.due_date, task.status);
  const isSub = level > 0;
  const isDone = task.status === "done";

  return (
    <div data-testid={`task-row-${task.id}`}>
      <div
        className={`flex items-start gap-3 py-3 ${
          isSub ? "pl-10 border-l-2 border-[#A8977B]/30 ml-3" : ""
        } ${isDone ? "opacity-60" : ""}`}
      >
        <button
          onClick={toggle}
          className={`mt-0.5 w-5 h-5 border flex items-center justify-center shrink-0 transition-colors ${
            isDone
              ? "bg-[#1A2F24] border-[#1A2F24]"
              : "bg-white border-[#1A2F24]/40 hover:border-[#1A2F24]"
          }`}
          data-testid={`task-toggle-${task.id}`}
        >
          {isDone && <Check size={12} weight="bold" className="text-[#F7F6F2]" />}
        </button>

        <div className="flex-1 min-w-0">
          {editing ? (
            <div className="space-y-2 fade-up">
              <input
                data-testid={`task-edit-title-${task.id}`}
                className="input-clean"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
              />
              <textarea
                rows={2}
                data-testid={`task-edit-desc-${task.id}`}
                className="input-clean text-sm"
                placeholder="Description (optional)"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
              />
              <div className="grid grid-cols-3 gap-2">
                <select
                  data-testid={`task-edit-assignee-${task.id}`}
                  className="input-clean text-xs"
                  value={form.assignee_id}
                  onChange={(e) => setForm({ ...form, assignee_id: e.target.value })}
                >
                  <option value="">Unassigned</option>
                  {members.map((m) => (
                    <option key={m.id} value={m.id}>
                      {m.name}
                    </option>
                  ))}
                </select>
                <input
                  data-testid={`task-edit-due-${task.id}`}
                  type="date"
                  className="input-clean text-xs"
                  value={form.due_date || ""}
                  onChange={(e) => setForm({ ...form, due_date: e.target.value })}
                />
                <select
                  data-testid={`task-edit-priority-${task.id}`}
                  className="input-clean text-xs"
                  value={form.priority}
                  onChange={(e) => setForm({ ...form, priority: e.target.value })}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
              </div>
              <div className="flex gap-2">
                <button
                  onClick={save}
                  className="btn-primary"
                  data-testid={`task-save-${task.id}`}
                >
                  <Check size={12} className="inline mr-1" /> Save
                </button>
                <button
                  onClick={() => {
                    setEditing(false);
                    setForm({
                      title: task.title,
                      description: task.description || "",
                      assignee_id: task.assignee_id || "",
                      due_date: task.due_date || "",
                      priority: task.priority,
                    });
                  }}
                  className="btn-outline"
                >
                  <X size={12} />
                </button>
              </div>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-2 flex-wrap">
                {!isSub && task.subtasks?.length > 0 && (
                  <button
                    onClick={() => setExpanded(!expanded)}
                    className="text-[#606C64] hover:text-[#1A2F24]"
                    data-testid={`task-collapse-${task.id}`}
                  >
                    {expanded ? <CaretDown size={12} /> : <CaretRight size={12} />}
                  </button>
                )}
                <p
                  className={`text-sm ${
                    isDone ? "line-through text-[#606C64]" : "text-[#1A2F24]"
                  }`}
                >
                  {task.title}
                </p>
                <button
                  onClick={cyclePriority}
                  className="px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-widest cursor-pointer"
                  style={{ background: tone.bg, color: tone.fg }}
                  title="Click to cycle priority"
                  data-testid={`task-priority-${task.id}`}
                >
                  {tone.label}
                </button>
                {task.due_date && (
                  <span
                    className={`label-eyebrow inline-flex items-center gap-1 ${
                      overdue ? "text-[#C94A3D]" : ""
                    }`}
                  >
                    <Calendar size={10} />
                    {formatDue(task.due_date)}
                    {overdue && " · overdue"}
                  </span>
                )}
                {task.assignee && (
                  <span
                    className="label-eyebrow inline-flex items-center gap-1.5"
                    title={task.assignee.name}
                  >
                    <span className="w-4 h-4 bg-[#1A2F24] text-[#F7F6F2] flex items-center justify-center font-mono text-[8px]">
                      {task.assignee.avatar}
                    </span>
                    {task.assignee.name?.split(" ")[0]}
                  </span>
                )}
                {!task.assignee && !editing && (
                  <span className="label-eyebrow inline-flex items-center gap-1 text-[#A8977B]">
                    <User size={10} /> unassigned
                  </span>
                )}
              </div>
              {task.description && (
                <p className="text-xs text-[#606C64] mt-1 italic">{task.description}</p>
              )}
            </>
          )}
        </div>

        {!editing && (
          <div className="flex gap-1 shrink-0">
            {!isSub && (
              <button
                onClick={() => onAddSubtask(task.id)}
                className="p-1.5 hover:bg-[#efece3]"
                title="Add subtask"
                data-testid={`task-add-sub-${task.id}`}
              >
                <Plus size={12} />
              </button>
            )}
            <button
              onClick={() => setEditing(true)}
              className="p-1.5 hover:bg-[#efece3]"
              data-testid={`task-edit-${task.id}`}
            >
              <PencilSimple size={12} />
            </button>
            <button
              onClick={remove}
              className="p-1.5 hover:bg-[#efece3] hover:text-[#C94A3D]"
              data-testid={`task-delete-${task.id}`}
            >
              <Trash size={12} />
            </button>
          </div>
        )}
      </div>

      {expanded && task.subtasks?.length > 0 && (
        <div>
          {task.subtasks.map((sub) =>
            React.createElement(TaskRow, {
              key: sub.id,
              task: sub,
              level: level + 1,
              onChange,
              onAddSubtask,
              members,
            }),
          )}
        </div>
      )}
    </div>
  );
};

export default TaskRow;
